/**
 * Permission Service
 * 
 * Handles role-based access control (RBAC) and permissions
 */

import { safeFrom, safeAuth, isSupabaseAvailable } from '@/lib/safe-supabase';
import { 
  ServiceResponse, 
  successResponse, 
  errorResponse, 
  handleServiceError,
  notConfiguredError
} from '@/lib/service-errors';
import type { 
  Role, 
  Permission, 
  UserRole, 
  RolePermission,
  UserPermissions,
  ContentApproval
} from '@/types/permission';

/**
 * Get user's roles
 */
export const getUserRoles = async (userId: string): Promise<ServiceResponse<Role[]>> => {
  if (!isSupabaseAvailable()) {
    return errorResponse(notConfiguredError('Supabase'));
  }

  try {
    const { data, error } = await safeFrom('user_roles')
      .select(`
        role_id,
        roles (
          id,
          name,
          display_name,
          description,
          is_system_role,
          is_default,
          metadata,
          created_at,
          updated_at
        )
      `)
      .eq('user_id', userId)
      .is('expires_at', null) // Only active roles (not expired)
      .or('expires_at.gt.' + new Date().toISOString());

    if (error) {
      return errorResponse({
        code: 'DATABASE_ERROR',
        message: `Failed to fetch user roles: ${error.message}`,
        recoverable: true,
        originalError: error
      });
    }

    const roles = (data || [])
      .map((item: any) => item.roles)
      .filter((role: any) => role !== null) as Role[];

    return successResponse(roles);
  } catch (error) {
    return handleServiceError(error, 'Failed to fetch user roles');
  }
};

/**
 * Get user's permissions (based on their roles)
 */
export const getUserPermissions = async (userId: string): Promise<ServiceResponse<UserPermissions>> => {
  if (!isSupabaseAvailable()) {
    return errorResponse(notConfiguredError('Supabase'));
  }

  try {
    // Get user roles
    const rolesResult = await getUserRoles(userId);
    if (!rolesResult.success || !rolesResult.data) {
      return errorResponse({
        code: 'NOT_FOUND',
        message: 'Failed to fetch user roles',
        recoverable: true
      });
    }

    const roles = rolesResult.data;
    const roleIds = roles.map(r => r.id);

    if (roleIds.length === 0) {
      return successResponse({
        userId,
        roles: [],
        permissions: [],
        hasPermission: () => false,
        hasRole: () => false,
      });
    }

    // Get permissions for these roles
    const { data: rolePermissions, error: rpError } = await safeFrom('role_permissions')
      .select(`
        permission_id,
        scope,
        permissions (
          id,
          name,
          display_name,
          description,
          category,
          action,
          metadata,
          created_at,
          updated_at
        )
      `)
      .in('role_id', roleIds);

    if (rpError) {
      return errorResponse({
        code: 'DATABASE_ERROR',
        message: `Failed to fetch permissions: ${rpError.message}`,
        recoverable: true,
        originalError: rpError
      });
    }

    const permissions = (rolePermissions || [])
      .map((item: any) => ({
        ...item.permissions,
        scope: item.scope || 'all'
      }))
      .filter((p: any) => p !== null) as (Permission & { scope: string })[];

    // Create permission map for quick lookup
    const permissionMap = new Map<string, string>();
    permissions.forEach(p => {
      permissionMap.set(p.name, p.scope);
    });

    const userPermissions: UserPermissions = {
      userId,
      roles,
      permissions: permissions.map(({ scope, ...p }) => p),
      hasPermission: (permissionName: string, scope?: 'own' | 'all') => {
        const userScope = permissionMap.get(permissionName);
        if (!userScope) return false;
        if (scope === 'all') {
          return userScope === 'all';
        }
        return true; // If user has 'all' scope, they can also do 'own'
      },
      hasRole: (roleName: string) => {
        return roles.some(r => r.name === roleName);
      },
    };

    return successResponse(userPermissions);
  } catch (error) {
    return handleServiceError(error, 'Failed to fetch user permissions');
  }
};

/**
 * Check if user has a specific permission
 */
export const hasPermission = async (
  userId: string,
  permissionName: string,
  scope?: 'own' | 'all'
): Promise<boolean> => {
  const permissionsResult = await getUserPermissions(userId);
  if (!permissionsResult.success || !permissionsResult.data) {
    return false;
  }

  return permissionsResult.data.hasPermission(permissionName, scope);
};

/**
 * Check if user has a specific role
 */
export const hasRole = async (userId: string, roleName: string): Promise<boolean> => {
  const permissionsResult = await getUserPermissions(userId);
  if (!permissionsResult.success || !permissionsResult.data) {
    return false;
  }

  return permissionsResult.data.hasRole(roleName);
};

/**
 * Get all roles (admin only)
 */
export const getRoles = async (): Promise<ServiceResponse<Role[]>> => {
  if (!isSupabaseAvailable()) {
    return errorResponse(notConfiguredError('Supabase'));
  }

  try {
    const { data, error } = await safeFrom('roles')
      .select('*')
      .order('name');

    if (error) {
      // Provide more helpful error messages based on error code
      let errorMessage = `Failed to fetch roles: ${error.message}`;
      let errorCode: ServiceErrorCode = 'DATABASE_ERROR';
      
      // Check for common RLS/permission errors
      if (error.message?.includes('permission denied') || error.message?.includes('row-level security')) {
        errorMessage = 'Access denied. You may not be in the admins table or RLS policies are blocking access.';
        errorCode = 'UNAUTHORIZED';
      } else if (error.message?.includes('relation') && error.message?.includes('does not exist')) {
        errorMessage = 'Roles table does not exist. Please run the migration: 20260121000002_create_roles_permissions.sql';
        errorCode = 'NOT_FOUND';
      }
      
      console.error('[getRoles] Database error:', error);
      console.error('[getRoles] Error details:', {
        message: error.message,
        code: error.code,
        details: error.details,
        hint: error.hint,
        fullError: JSON.stringify(error, null, 2)
      });
      
      return errorResponse({
        code: errorCode,
        message: errorMessage,
        recoverable: true,
        originalError: error
      });
    }

    // Transform database format (snake_case) to TypeScript format (camelCase)
    const roles: Role[] = (data || []).map((item: any) => ({
      id: item.id,
      name: item.name,
      displayName: item.display_name,
      description: item.description,
      isSystemRole: item.is_system_role,
      isDefault: item.is_default,
      metadata: item.metadata,
      createdAt: item.created_at,
      updatedAt: item.updated_at,
    }));

    return successResponse(roles);
  } catch (error) {
    return handleServiceError(error, 'Failed to fetch roles');
  }
};

/**
 * Get all permissions (admin only)
 */
export const getPermissions = async (): Promise<ServiceResponse<Permission[]>> => {
  if (!isSupabaseAvailable()) {
    return errorResponse(notConfiguredError('Supabase'));
  }

  try {
    const { data, error } = await safeFrom('permissions')
      .select('*')
      .order('category, action');

    if (error) {
      return errorResponse({
        code: 'DATABASE_ERROR',
        message: `Failed to fetch permissions: ${error.message}`,
        recoverable: true,
        originalError: error
      });
    }

    return successResponse((data || []) as Permission[]);
  } catch (error) {
    return handleServiceError(error, 'Failed to fetch permissions');
  }
};

/**
 * Get permissions for a specific role
 */
export const getRolePermissions = async (roleId: number): Promise<ServiceResponse<(Permission & { scope: string })[]>> => {
  if (!isSupabaseAvailable()) {
    return errorResponse(notConfiguredError('Supabase'));
  }

  try {
    const { data, error } = await safeFrom('role_permissions')
      .select(`
        permission_id,
        scope,
        permissions (
          id,
          name,
          display_name,
          description,
          category,
          action,
          metadata,
          created_at,
          updated_at
        )
      `)
      .eq('role_id', roleId);

    if (error) {
      return errorResponse({
        code: 'DATABASE_ERROR',
        message: `Failed to fetch role permissions: ${error.message}`,
        recoverable: true,
        originalError: error
      });
    }

    const rolePerms = (data || [])
      .map((item: any) => ({
        ...item.permissions,
        scope: item.scope || 'all'
      }))
      .filter((p: any) => p !== null) as (Permission & { scope: string })[];

    return successResponse(rolePerms);
  } catch (error) {
    return handleServiceError(error, 'Failed to fetch role permissions');
  }
};

/**
 * Assign permission to role
 */
export const assignPermissionToRole = async (
  roleId: number,
  permissionId: number,
  scope: 'own' | 'all' = 'all'
): Promise<ServiceResponse<RolePermission>> => {
  if (!isSupabaseAvailable()) {
    return errorResponse(notConfiguredError('Supabase'));
  }

  try {
    const { data, error } = await safeFrom('role_permissions')
      .insert({
        role_id: roleId,
        permission_id: permissionId,
        scope: scope,
      })
      .select()
      .single();

    if (error) {
      return errorResponse({
        code: 'DATABASE_ERROR',
        message: `Failed to assign permission: ${error.message}`,
        recoverable: true,
        originalError: error
      });
    }

    return successResponse(data as RolePermission);
  } catch (error) {
    return handleServiceError(error, 'Failed to assign permission to role');
  }
};

/**
 * Remove permission from role
 */
export const removePermissionFromRole = async (
  roleId: number,
  permissionId: number,
  scope?: string
): Promise<ServiceResponse<void>> => {
  if (!isSupabaseAvailable()) {
    return errorResponse(notConfiguredError('Supabase'));
  }

  try {
    let query = safeFrom('role_permissions')
      .delete()
      .eq('role_id', roleId)
      .eq('permission_id', permissionId);

    if (scope) {
      query = query.eq('scope', scope);
    }

    const { error } = await query;

    if (error) {
      return errorResponse({
        code: 'DATABASE_ERROR',
        message: `Failed to remove permission: ${error.message}`,
        recoverable: true,
        originalError: error
      });
    }

    return successResponse(undefined);
  } catch (error) {
    return handleServiceError(error, 'Failed to remove permission from role');
  }
};

/**
 * Create a new role
 */
export const createRole = async (roleData: {
  name: string;
  displayName: string;
  description?: string;
  metadata?: Record<string, any>;
}): Promise<ServiceResponse<Role>> => {
  if (!isSupabaseAvailable()) {
    return errorResponse(notConfiguredError('Supabase'));
  }

  try {
    // Validate name format (lowercase, alphanumeric, underscores only)
    if (!/^[a-z0-9_]+$/.test(roleData.name)) {
      return errorResponse({
        code: 'VALIDATION_ERROR',
        message: 'Role name must be lowercase, alphanumeric, and may contain underscores only',
        recoverable: true
      });
    }

    // Check if role name already exists
    const { data: existing } = await safeFrom('roles')
      .select('id')
      .eq('name', roleData.name)
      .single();

    if (existing) {
      return errorResponse({
        code: 'DUPLICATE_ERROR',
        message: `A role with the name "${roleData.name}" already exists`,
        recoverable: true
      });
    }

    const { data, error } = await safeFrom('roles')
      .insert({
        name: roleData.name,
        display_name: roleData.displayName,
        description: roleData.description || null,
        is_system_role: false,
        is_default: false,
        metadata: roleData.metadata || null,
      })
      .select()
      .single();

    if (error) {
      return errorResponse({
        code: 'DATABASE_ERROR',
        message: `Failed to create role: ${error.message}`,
        recoverable: true,
        originalError: error
      });
    }

    // Transform to match Role type
    const role: Role = {
      id: data.id,
      name: data.name,
      displayName: data.display_name,
      description: data.description,
      isSystemRole: data.is_system_role,
      isDefault: data.is_default,
      metadata: data.metadata,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };

    return successResponse(role);
  } catch (error) {
    return handleServiceError(error, 'Failed to create role');
  }
};

/**
 * Update an existing role
 */
export const updateRole = async (
  roleId: number,
  roleData: {
    displayName?: string;
    description?: string;
    metadata?: Record<string, any>;
  }
): Promise<ServiceResponse<Role>> => {
  if (!isSupabaseAvailable()) {
    return errorResponse(notConfiguredError('Supabase'));
  }

  try {
    // Check if role exists and is not a system role
    const { data: existing, error: fetchError } = await safeFrom('roles')
      .select('*')
      .eq('id', roleId)
      .single();

    if (fetchError || !existing) {
      return errorResponse({
        code: 'NOT_FOUND',
        message: 'Role not found',
        recoverable: true,
        originalError: fetchError
      });
    }

    if (existing.is_system_role) {
      return errorResponse({
        code: 'PERMISSION_DENIED',
        message: 'System roles cannot be modified',
        recoverable: true
      });
    }

    // Build update object
    const updateData: any = {
      updated_at: new Date().toISOString(),
    };

    if (roleData.displayName !== undefined) {
      updateData.display_name = roleData.displayName;
    }
    if (roleData.description !== undefined) {
      updateData.description = roleData.description || null;
    }
    if (roleData.metadata !== undefined) {
      updateData.metadata = roleData.metadata || null;
    }

    const { data, error } = await safeFrom('roles')
      .update(updateData)
      .eq('id', roleId)
      .select()
      .single();

    if (error) {
      return errorResponse({
        code: 'DATABASE_ERROR',
        message: `Failed to update role: ${error.message}`,
        recoverable: true,
        originalError: error
      });
    }

    // Transform to match Role type
    const role: Role = {
      id: data.id,
      name: data.name,
      displayName: data.display_name,
      description: data.description,
      isSystemRole: data.is_system_role,
      isDefault: data.is_default,
      metadata: data.metadata,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };

    return successResponse(role);
  } catch (error) {
    return handleServiceError(error, 'Failed to update role');
  }
};

/**
 * Delete a role
 */
export const deleteRole = async (roleId: number): Promise<ServiceResponse<void>> => {
  if (!isSupabaseAvailable()) {
    return errorResponse(notConfiguredError('Supabase'));
  }

  try {
    // Check if role exists and is not a system role
    const { data: existing, error: fetchError } = await safeFrom('roles')
      .select('is_system_role')
      .eq('id', roleId)
      .single();

    if (fetchError || !existing) {
      return errorResponse({
        code: 'NOT_FOUND',
        message: 'Role not found',
        recoverable: true,
        originalError: fetchError
      });
    }

    if (existing.is_system_role) {
      return errorResponse({
        code: 'PERMISSION_DENIED',
        message: 'System roles cannot be deleted',
        recoverable: true
      });
    }

    // Check if role is assigned to any users
    const { data: userRoles, error: userRolesError } = await safeFrom('user_roles')
      .select('id')
      .eq('role_id', roleId)
      .limit(1);

    if (userRolesError) {
      return errorResponse({
        code: 'DATABASE_ERROR',
        message: `Failed to check role assignments: ${userRolesError.message}`,
        recoverable: true,
        originalError: userRolesError
      });
    }

    if (userRoles && userRoles.length > 0) {
      return errorResponse({
        code: 'CONFLICT',
        message: 'Cannot delete role: It is assigned to one or more users. Remove all assignments first.',
        recoverable: true
      });
    }

    // Delete role permissions first (CASCADE should handle this, but being explicit)
    await safeFrom('role_permissions')
      .delete()
      .eq('role_id', roleId);

    // Delete the role
    const { error } = await safeFrom('roles')
      .delete()
      .eq('id', roleId);

    if (error) {
      return errorResponse({
        code: 'DATABASE_ERROR',
        message: `Failed to delete role: ${error.message}`,
        recoverable: true,
        originalError: error
      });
    }

    return successResponse(undefined);
  } catch (error) {
    return handleServiceError(error, 'Failed to delete role');
  }
};

/**
 * Assign role to user (super admin only)
 */
export const assignRole = async (
  userId: string,
  roleId: number,
  assignedBy?: string
): Promise<ServiceResponse<UserRole>> => {
  if (!isSupabaseAvailable()) {
    return errorResponse(notConfiguredError('Supabase'));
  }

  try {
    const { data: currentUser } = await safeAuth.getUser();
    const assignedById = assignedBy || currentUser?.user?.id;

    const { data, error } = await safeFrom('user_roles')
      .insert({
        user_id: userId,
        role_id: roleId,
        assigned_by: assignedById,
      })
      .select()
      .single();

    if (error) {
      return errorResponse({
        code: 'DATABASE_ERROR',
        message: `Failed to assign role: ${error.message}`,
        recoverable: true,
        originalError: error
      });
    }

    return successResponse(data as UserRole);
  } catch (error) {
    return handleServiceError(error, 'Failed to assign role');
  }
};

/**
 * Remove role from user (super admin only)
 */
export const removeRole = async (
  userId: string,
  roleId: number
): Promise<ServiceResponse<void>> => {
  if (!isSupabaseAvailable()) {
    return errorResponse(notConfiguredError('Supabase'));
  }

  try {
    const { error } = await safeFrom('user_roles')
      .delete()
      .eq('user_id', userId)
      .eq('role_id', roleId);

    if (error) {
      return errorResponse({
        code: 'DATABASE_ERROR',
        message: `Failed to remove role: ${error.message}`,
        recoverable: true,
        originalError: error
      });
    }

    return successResponse(undefined);
  } catch (error) {
    return handleServiceError(error, 'Failed to remove role');
  }
};

/**
 * Log user activity
 */
export const logActivity = async (
  userId: string,
  action: string,
  resourceType: string,
  resourceId?: number,
  changes?: Record<string, any>,
  description?: string
): Promise<ServiceResponse<UserActivityLog>> => {
  if (!isSupabaseAvailable()) {
    return errorResponse(notConfiguredError('Supabase'));
  }

  try {
    // Get IP address and user agent from browser
    const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : null;

    const { data, error } = await safeFrom('user_activity_logs')
      .insert({
        user_id: userId,
        action,
        resource_type: resourceType,
        resource_id: resourceId || null,
        changes: changes || null,
        description: description || null,
        user_agent: userAgent,
      })
      .select()
      .single();

    if (error) {
      return errorResponse({
        code: 'DATABASE_ERROR',
        message: `Failed to log activity: ${error.message}`,
        recoverable: true,
        originalError: error
      });
    }

    return successResponse(data as UserActivityLog);
  } catch (error) {
    return handleServiceError(error, 'Failed to log activity');
  }
};

/**
 * Submit content for approval
 */
export const submitForApproval = async (
  resourceType: string,
  resourceId: number,
  submittedBy: string
): Promise<ServiceResponse<ContentApproval>> => {
  if (!isSupabaseAvailable()) {
    return errorResponse(notConfiguredError('Supabase'));
  }

  try {
    const { data, error } = await safeFrom('content_approvals')
      .insert({
        resource_type: resourceType,
        resource_id: resourceId,
        submitted_by: submittedBy,
        status: 'pending',
      })
      .select()
      .single();

    if (error) {
      return errorResponse({
        code: 'DATABASE_ERROR',
        message: `Failed to submit for approval: ${error.message}`,
        recoverable: true,
        originalError: error
      });
    }

    return successResponse(data as ContentApproval);
  } catch (error) {
    return handleServiceError(error, 'Failed to submit for approval');
  }
};

/**
 * Review content approval
 */
export const reviewApproval = async (
  approvalId: number,
  status: 'approved' | 'rejected' | 'changes_requested',
  reviewedBy: string,
  reviewNotes?: string
): Promise<ServiceResponse<ContentApproval>> => {
  if (!isSupabaseAvailable()) {
    return errorResponse(notConfiguredError('Supabase'));
  }

  try {
    const { data, error } = await safeFrom('content_approvals')
      .update({
        status,
        reviewed_by: reviewedBy,
        review_notes: reviewNotes || null,
        reviewed_at: new Date().toISOString(),
      })
      .eq('id', approvalId)
      .select()
      .single();

    if (error) {
      return errorResponse({
        code: 'DATABASE_ERROR',
        message: `Failed to review approval: ${error.message}`,
        recoverable: true,
        originalError: error
      });
    }

    return successResponse(data as ContentApproval);
  } catch (error) {
    return handleServiceError(error, 'Failed to review approval');
  }
};

export const permissionService = {
  getUserRoles,
  getUserPermissions,
  hasPermission,
  hasRole,
  getRoles,
  getPermissions,
  getRolePermissions,
  createRole,
  updateRole,
  deleteRole,
  assignRole,
  removeRole,
  assignPermissionToRole,
  removePermissionFromRole,
  logActivity,
  submitForApproval,
  reviewApproval,
};
